import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { nodesToPdf } from '../utils/pdf';
import { LEVEL_COLOURS } from '../utils/score';
import {
  YES_TIERS, flattenIndicators, scenarioResult, shortfall, autoAllocate, toRealWorld,
  clampAddition, targetOptions, defaultYesTier, totalAdded, actionSummary,
} from '../utils/scenario';
import '../styles/ScenarioPlanner.scss';
import Spinner from './Spinner';

const fmtPts = (n) => (Math.round((n || 0) * 100) / 100).toFixed(2);
const fmtRand = (n) => `R ${Math.round(n).toLocaleString('en-ZA')}`;

const GROUP_LABELS = {
  ownership: 'Ownership',
  skills: 'Skills Development',
  esd: 'Enterprise & Supplier Development',
};

const DEMO_LABELS = {
  african_male: 'African male',
  african_female: 'African female',
  coloured_male: 'Coloured male',
  coloured_female: 'Coloured female',
  indian_male: 'Indian male',
  indian_female: 'Indian female',
};

const realWorldSummary = (rw) => (
  rw.kind === 'spend' ? `+${fmtRand(rw.value)} spend`
    : rw.kind === 'pct' ? `+${rw.value} percentage points`
    : `~${rw.value} more ${rw.value === 1 ? 'person' : 'people'} (estimate)`
);

const DemoList = ({ rw }) => {
  if (!rw.breakdown?.length) return null;
  const isSpend = rw.kind === 'spend';
  return (
    <ul className="sp-demo-list">
      {rw.breakdown.map((d) => (
        <li key={d.key}>
          {DEMO_LABELS[d.key] || d.key}: {isSpend ? `+${fmtRand(d.value)}` : `+${d.value}${d.value === 1 ? ' person' : ' people'}`}
        </li>
      ))}
    </ul>
  );
};

const describeRealWorld = (item, added) => {
  if (!added) return '—';
  const rw = toRealWorld(item, added);
  if (!rw) return 'Points only';
  return <>{realWorldSummary(rw)}<DemoList rw={rw} /></>;
};

// Bottom-of-page list of everything the scenario needs, grouped by element
const ActionSummary = ({ summary, target }) => {
  const { elements, totals, subMin, yes, result } = summary;
  return (
    <div className="sp-actions-summary">
      <h3 className="sp-section-title">Action plan{target ? ` to reach ${target}` : ''}</h3>

      <div className="sp-action-totals">
        <span><strong>{fmtPts(totals.points)}</strong> points added</span>
        <span>Result: <LevelChip level={result.level} /> {fmtPts(result.finalPoints)} points</span>
        {totals.rand > 0 && <span><strong>{fmtRand(totals.rand)}</strong> total spend</span>}
        {totals.people > 0 && <span><strong>~{totals.people}</strong> more {totals.people === 1 ? 'person' : 'people'} (estimate)</span>}
      </div>

      {(subMin.fixed.length > 0 || subMin.failing.length > 0) && (
        <div className="sp-action-group">
          <h4>Sub-minimums</h4>
          <ul>
            {subMin.fixed.map((g) => <li key={g}>{GROUP_LABELS[g]} sub-minimum is met by the actions below</li>)}
            {subMin.failing.map((g) => <li key={g} className="sp-action-warn">{GROUP_LABELS[g]} sub-minimum is still not met (costs one level)</li>)}
          </ul>
        </div>
      )}

      {elements.map((el) => (
        <div key={el.id} className="sp-action-group">
          <h4>{el.label}</h4>
          <ul>
            {el.actions.map((a) => (
              <li key={a.key}>
                <strong>{a.label}</strong>{a.bonus && <span className="sp-tag">bonus</span>}:{' '}
                {a.realWorld ? `${realWorldSummary(a.realWorld)} ` : ''}
                <span className="sp-muted">(+{fmtPts(a.points)} pts)</span>
                {a.realWorld && <DemoList rw={a.realWorld} />}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {yes && (
        <div className="sp-action-group">
          <h4>Y.E.S</h4>
          <ul>
            {yes.register && <li>Register for Y.E.S</li>}
            <li>
              {YES_TIERS[yes.tier].label.split(' - ')[0]} needs {yes.required} eligible placements
              {' '}— you have {yes.current}
              {yes.more > 0 ? `, so ${yes.more} more ${yes.more === 1 ? 'is' : 'are'} needed` : ', so the placement target is met'}
            </li>
            <li>
              Absorption of at least {yes.minAbsorptionPct}% — currently {yes.absorptionPct}%
              {yes.absorptionPct >= yes.minAbsorptionPct ? ' (met)' : ''}
            </li>
            {result.yesBlocked && (
              <li className="sp-action-warn">
                Y.E.S won't count until the level before Y.E.S holds the level recorded on Company Info
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const LevelChip = ({ level }) => (
  <span className="sp-level" style={{ background: LEVEL_COLOURS[level] || 'var(--ink-3)' }}>{level || '—'}</span>
);

// What-if tool: models the points needed to reach a higher B-BBEE level.
// Read-only — it never changes the saved scorecard. Scenarios aren't stored; download the PDF to keep one.
const ScenarioPlanner = ({ companyName }) => {
  const [status, setStatus] = useState('loading');   // loading | ready | error
  const [baseline, setBaseline] = useState(null);
  const [target, setTarget] = useState('');
  const [yesTier, setYesTier] = useState('none');
  const [mode, setMode] = useState('gap');
  const [additions, setAdditions] = useState({});
  const [drafts, setDrafts] = useState({});
  const [locked, setLocked] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);           // 'suggest' | 'pdf' while that action runs
  const sheetRef = useRef(null);

  useEffect(() => {
    api.get('/core/scorecard/scenario-baseline/')
      .then((res) => {
        setBaseline(res.data);
        setYesTier(defaultYesTier(res.data));
        setTarget(targetOptions(res.data, res.data.current.level)[0] || '');
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  const items = useMemo(() => (baseline ? flattenIndicators(baseline) : []), [baseline]);

  // Rows grouped by element, in backend order.
  const sections = useMemo(() => {
    if (!baseline) return [];
    const calculated = {};
    baseline.elements.forEach((g) => g.elements.forEach((el) => { calculated[el.id] = el.calculated; }));
    const map = new Map();
    items.forEach((item) => {
      if (!map.has(item.elementId)) {
        map.set(item.elementId, { id: item.elementId, label: item.elementLabel, calculated: calculated[item.elementId], items: [] });
      }
      map.get(item.elementId).items.push(item);
    });
    return [...map.values()];
  }, [baseline, items]);

  const priorityGroups = useMemo(
    () => [...new Set(items.map((i) => i.priorityGroup).filter(Boolean))],
    [items],
  );

  const result = useMemo(
    () => (baseline ? scenarioResult(baseline, additions, yesTier) : null),
    [baseline, additions, yesTier],
  );
  const startGap = useMemo(
    () => (baseline && target ? shortfall(baseline, {}, target, yesTier) : null),
    [baseline, target, yesTier],
  );
  const gap = useMemo(
    () => (baseline && target ? shortfall(baseline, additions, target, yesTier) : null),
    [baseline, additions, target, yesTier],
  );
  const actions = useMemo(
    () => (baseline ? actionSummary(baseline, additions, yesTier) : null),
    [baseline, additions, yesTier],
  );

  if (status === 'loading') return <div className="sp-empty"><Spinner label="Loading scenario data…" /></div>;
  if (status === 'error') return <div className="sp-empty">Could not load scenario data. Please try again.</div>;

  const options = targetOptions(baseline, baseline.current.level);

  const setAddition = (item, raw) => {
    setDrafts((d) => ({ ...d, [item.key]: raw }));
    setAdditions((prev) => {
      const next = { ...prev };
      const value = clampAddition(item, raw);
      if (value > 0) next[item.key] = value;
      else delete next[item.key];
      return next;
    });
  };

  const clearDraft = (key) => setDrafts(({ [key]: _, ...rest }) => rest);

  const toggleLock = (item) => {
    const isLocked = locked.includes(item.key);
    setLocked(isLocked ? locked.filter((k) => k !== item.key) : [...locked, item.key]);
    if (!isLocked) {
      // Locking means "don't touch this one" — clear any points already on it.
      setAdditions(({ [item.key]: _, ...rest }) => rest);
      clearDraft(item.key);
    }
  };

  // Mark the action busy (its button shows "Calculating…" etc.), then run the work on the
  // next frame so the button updates before a heavy calculation or PDF capture blocks the page.
  const runBusy = (action, work) => {
    setBusy(action);
    requestAnimationFrame(() => setTimeout(async () => {
      try {
        await work();
      } finally {
        setBusy(null);
      }
    }, 0));
  };

  const runAutoSuggest = () => runBusy('suggest', () => {
    const { additions: next, reached } = autoAllocate(baseline, target, { mode, locked, yesTier });
    setAdditions(next);
    setDrafts({});
    setNotice(reached ? null : `${target} can't be reached with the unlocked indicators. Unlock more indicators or pick a closer level.`);
  });

  const reset = () => {
    setAdditions({});
    setDrafts({});
    setNotice(null);
  };

  const handleDownload = () => runBusy('pdf', () => {
    const node = sheetRef.current;
    return nodesToPdf(node, `bbee-scenario-${target.toLowerCase().replace(/\s+/g, '-')}.pdf`, {
      keepSelector: '.sp-banner, .sp-stat, .sp-submin',
      beforeCapture: () => node.classList.add('sp-capturing'),
      afterCapture: () => node.classList.remove('sp-capturing'),
    });
  });

  const yes = baseline.yes;
  const tierInfo = YES_TIERS[yesTier];

  return (
    <div className="sp" ref={sheetRef} aria-busy={Boolean(busy)}>
      <div className="sp-banner">For planning purposes only — this does not change your scorecard.</div>

      {companyName && <h2 className="sp-title">{companyName} — Scenario Planning</h2>}

      {/* ── Controls ── */}
      <div className="sp-controls sp-no-print">
        <label className="sp-field">
          <span>Target level</span>
          <select value={target} onChange={(e) => { setTarget(e.target.value); setNotice(null); }} disabled={!options.length}>
            {options.length === 0 && <option value="">Already at the top level</option>}
            {options.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>

        {yes && (
          <label className="sp-field">
            <span>Y.E.S recognition</span>
            <select value={yesTier} onChange={(e) => setYesTier(e.target.value)}>
              {Object.entries(YES_TIERS).map(([key, t]) => <option key={key} value={key}>{t.label}</option>)}
            </select>
          </label>
        )}

        <div className="sp-field">
          <span>Auto-suggest order</span>
          <div className="sp-toggle">
            <button type="button" className={mode === 'gap' ? 'is-on' : ''} onClick={() => setMode('gap')}>Largest gap</button>
            <button type="button" className={mode === 'rand' ? 'is-on' : ''} onClick={() => setMode('rand')}>Cheapest Rand</button>
          </div>
        </div>

        <div className="sp-actions">
          <button type="button" className="sp-btn sp-btn--primary" onClick={runAutoSuggest} disabled={!target || Boolean(busy)}>
            {busy === 'suggest' ? 'Calculating…' : 'Auto-suggest'}
          </button>
          <button type="button" className="sp-btn" onClick={reset} disabled={Boolean(busy)}>Reset</button>
          <button type="button" className="sp-btn" onClick={handleDownload} disabled={Boolean(busy)}>
            {busy === 'pdf' ? 'Generating PDF…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {yes && yesTier !== 'none' && (
        <p className="sp-hint">
          Y.E.S target: {yes.target} placements · current eligible placements: {yes.eligible_placements}.
          {!yes.registered && ' Assumes the company registers for Y.E.S.'}
        </p>
      )}
      {result.yesBlocked && (
        <p className="sp-warn">
          Y.E.S wouldn't count: the level before Y.E.S ({result.levelBeforeYes}) is below the level recorded on
          Company Info ({yes.baseline_level}).
        </p>
      )}
      {gap?.blockedBySubMin && (
        <p className="sp-warn">A failed sub-minimum must be fixed before {target} is reachable.</p>
      )}
      {notice && <p className="sp-warn">{notice}</p>}

      {/* ── Summary ── */}
      <div className="sp-summary">
        <div className="sp-stat">
          <span className="sp-stat-label">Current</span>
          <LevelChip level={baseline.current.level} />
          <span className="sp-stat-sub">{fmtPts(baseline.current.points)} points</span>
        </div>
        <div className="sp-stat">
          <span className="sp-stat-label">Target</span>
          <LevelChip level={target} />
          <span className="sp-stat-sub">
            {startGap?.pointsNeeded != null ? `${fmtPts(startGap.pointsNeeded)} points short` : '—'}
          </span>
        </div>
        <div className="sp-stat">
          <span className="sp-stat-label">Allocated</span>
          <span className="sp-stat-big">{fmtPts(totalAdded(additions))}</span>
          <span className="sp-stat-sub">
            {!target ? '—' : gap?.reached ? 'Target reached' : `${fmtPts(gap?.pointsNeeded)} still needed`}
          </span>
        </div>
        <div className="sp-stat">
          <span className="sp-stat-label">Scenario result</span>
          <LevelChip level={result.level} />
          <span className="sp-stat-sub">
            {fmtPts(result.finalPoints)} points{tierInfo.levelsUp > 0 && !result.yesBlocked ? ' incl. Y.E.S' : ''}
          </span>
        </div>
      </div>

      {priorityGroups.length > 0 && (
        <div className="sp-submins">
          {priorityGroups.map((g) => (
            <span key={g} className={`sp-submin ${result.subMin[g] ? 'is-ok' : 'is-fail'}`}>
              {result.subMin[g] ? '✓' : '✕'} {GROUP_LABELS[g]} sub-minimum
            </span>
          ))}
        </div>
      )}

      {/* ── Indicator table ── */}
      {sections.map((section) => (
        <div key={section.id} className="sp-section">
          <h3 className="sp-section-title">
            {section.label}
            {!section.calculated && <span className="sp-tag">Not calculated yet — current points shown as 0</span>}
          </h3>
          <div className="sp-table-wrap">
            <table className="sp-table">
              <thead>
                <tr>
                  <th className="sp-no-print">Lock</th>
                  <th>Indicator</th>
                  <th className="sp-num">Current / Max</th>
                  <th className="sp-num">Add points</th>
                  <th className="sp-num">New points</th>
                  <th className="sp-rw-col">What it takes</th>
                </tr>
              </thead>
              <tbody>
                {section.items.map((item) => {
                  const added = additions[item.key] || 0;
                  const isLocked = locked.includes(item.key);
                  const full = item.room <= 0;
                  return (
                    <tr key={item.key} className={`${added > 0 ? 'sp-row--added' : ''} ${isLocked ? 'sp-row--locked' : ''}`}>
                      <td className="sp-no-print">
                        <input type="checkbox" checked={isLocked} onChange={() => toggleLock(item)} disabled={full} aria-label={`Lock ${item.label}`} />
                      </td>
                      <td>
                        {item.label}
                        {item.sub_minimum_pct != null && <span className="sp-tag">sub-minimum</span>}
                        {item.bonus && <span className="sp-tag">bonus</span>}
                      </td>
                      <td className="sp-num">{fmtPts(item.current_points)} / {fmtPts(item.max_points)}</td>
                      <td className="sp-num">
                        {full ? <span className="sp-muted">Full</span> : (
                          <input
                            type="number"
                            className="sp-input"
                            min="0"
                            max={item.room}
                            step={item.wholeOnly ? 1 : 0.01}
                            value={drafts[item.key] ?? (added || '')}
                            onChange={(e) => setAddition(item, e.target.value)}
                            onBlur={() => clearDraft(item.key)}
                            disabled={isLocked}
                            placeholder="0"
                            aria-label={`Points to add to ${item.label}`}
                          />
                        )}
                      </td>
                      <td className="sp-num">{fmtPts(item.current_points + added)}</td>
                      <td className="sp-rw sp-rw-col">{describeRealWorld(item, added)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {actions?.elements.length > 0 && <ActionSummary summary={actions} target={target} />}

      <p className="sp-footnote">
        Rand and headcount figures are estimates based on the targets and your saved TMPS, NPAT, leviable amount
        and headcount. EAP-weighted indicators are split by demographic group in proportion to each group's remaining gap.
      </p>
    </div>
  );
};

export default ScenarioPlanner;
